import { useState, useEffect } from 'react'
import { Bell, Search, Menu, User, LogOut, CreditCard, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUser, useSidebarCollapsed, useAppStore } from '@/store'
import { getInitials } from '@/lib/utils'
import dayjs from 'dayjs'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Tooltip } from '@/components/Tooltip'
import {
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from '@/components/Dropdown'
import * as Popover from '@radix-ui/react-popover'
import { useNavigate, useLocation } from 'react-router-dom'
import { authApi } from '@/lib/api'
import { CommandPalette } from '@/components/CommandPalette'

// ── Page title lookup ────────────────────────────────────────────────────────
const PAGE_TITLES: Record<string, { label: string; sanskrit?: string }> = {
  '/dashboard':     { label: 'Dashboard',      sanskrit: 'मुख्य' },
  '/daily-timings': { label: 'Daily Timings',   sanskrit: 'चौघड़िया' },
  '/birth-chart':   { label: 'Birth Chart',     sanskrit: 'जन्म' },
  '/dasha':         { label: 'Dasha Timeline',  sanskrit: 'दशा' },
  '/muhurta':       { label: 'Muhurta',         sanskrit: 'मुहूर्त' },
  '/event-muhurta': { label: 'Event Finder',    sanskrit: 'शुभ' },
  '/compatibility': { label: 'Kundali Match',   sanskrit: 'कुंडली' },
  '/insights':      { label: 'Influences',      sanskrit: 'ग्रह' },
  '/remedies':      { label: 'Remedies',        sanskrit: 'उपाय' },
  '/settings':      { label: 'Settings' },
  '/billing':       { label: 'Billing' },
  '/developer':     { label: 'API Dashboard',   sanskrit: 'API' },
  '/admin/users':   { label: 'Admin · Users' },
  '/admin/content': { label: 'Admin · Content' },
  '/admin/health':  { label: 'Admin · Health' },
}

// ── Sample notifications ─────────────────────────────────────────────────────
const SAMPLE_NOTIFICATIONS = [
  {
    id: '1',
    title: 'Rahu Kaal starting soon',
    body: 'Today\'s Rahu Kaal begins at 3:00 PM. Avoid important work.',
    time: '5m ago',
    read: false,
  },
  {
    id: '2',
    title: 'Auspicious muhurta tomorrow',
    body: 'Tomorrow at 7:12 AM is highly auspicious for new ventures.',
    time: '1h ago',
    read: false,
  },
  {
    id: '3',
    title: 'Dasha period changed',
    body: 'Your Moon Dasha sub-period begins in 3 days.',
    time: '2d ago',
    read: true,
  },
]

export function Header() {
  const user = useUser()
  const collapsed = useSidebarCollapsed()
  const { openMobileSidebar, logout } = useAppStore()
  const navigate = useNavigate()
  const location = useLocation()

  const [cmdOpen, setCmdOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifications, setNotifications] = useState(SAMPLE_NOTIFICATIONS)

  const now = dayjs()
  const greeting = now.hour() < 12 ? 'Good morning' : now.hour() < 17 ? 'Good afternoon' : 'Good evening'

  // Current page meta
  const pageMeta = PAGE_TITLES[location.pathname]

  const unreadCount = notifications.filter(n => !n.read).length

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  async function handleSignOut() {
    try {
      await authApi.logout()
    } catch {
      // ignore — log out locally regardless
    }
    logout()
    navigate('/login', { replace: true })
  }

  const markAllRead = () => {
    setNotifications(ns => ns.map(n => ({ ...n, read: true })))
  }

  return (
    <>
      <header
        className={cn(
          'fixed top-0 right-0 z-20 h-[var(--header-height)]',
          'flex items-center justify-between px-4 gap-3',
          'bg-cosmic-void/80 backdrop-blur-md border-b border-white/[0.06]',
          'transition-[left] duration-300 ease-cosmic',
          // Mobile: full width
          'left-0',
          // Desktop: offset by sidebar
          collapsed ? 'md:left-[60px]' : 'md:left-[var(--sidebar-width)]',
        )}
      >
        {/* ── Left section ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 min-w-0">
          {/* Hamburger — mobile only */}
          <button
            onClick={openMobileSidebar}
            className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-star-muted
                       hover:bg-cosmic-dusk hover:text-star-white transition-colors flex-shrink-0"
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Page context */}
          <div className="min-w-0">
            {pageMeta ? (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="text-star-bright text-sm font-semibold leading-none tracking-tight truncate">
                    {pageMeta.label}
                  </h1>
                  {pageMeta.sanskrit && (
                    <span className="hidden sm:block text-2xs font-display text-gold/50 leading-none">
                      {pageMeta.sanskrit}
                    </span>
                  )}
                </div>
                <p className="text-star-dim text-2xs font-mono mt-0.5 truncate">
                  {greeting}{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''} · {now.format('D MMM')}
                </p>
              </>
            ) : (
              <>
                <p className="text-star-white text-sm font-medium truncate">
                  {greeting}{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}
                </p>
                <p className="text-star-dim text-2xs font-mono">
                  {now.format('dddd, D MMMM YYYY')}
                </p>
              </>
            )}
          </div>

          {/* Dasha indicator pill */}
          {user?.dashaIndicator && (
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-800/60 border border-gold/20 animate-pulse-gold">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              <span className="text-gold text-2xs font-mono">{user.dashaIndicator}</span>
            </div>
          )}
        </div>

        {/* ── Right section ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Search / Command palette trigger */}
          <Tooltip content={<span>Search <kbd className="ml-1 opacity-60">⌘K</kbd></span>} side="bottom">
            <button
              onClick={() => setCmdOpen(true)}
              className={cn(
                'hidden sm:flex items-center gap-2 h-8 px-3 rounded-lg',
                'bg-cosmic-dusk/50 border border-white/[0.06] text-star-muted',
                'hover:bg-cosmic-dusk hover:text-star-soft hover:border-white/10',
                'transition-all duration-150 group',
              )}
              aria-label="Search (⌘K)"
            >
              <Search className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs hidden md:block">Search</span>
              <kbd className="hidden lg:flex items-center gap-0.5 text-2xs font-mono text-star-dim/60 ml-1">
                ⌘K
              </kbd>
            </button>
          </Tooltip>

          {/* Search icon only on small mobile */}
          <button
            onClick={() => setCmdOpen(true)}
            className="sm:hidden w-8 h-8 rounded-lg flex items-center justify-center text-star-muted
                       hover:bg-cosmic-dusk hover:text-star-white transition-colors"
            aria-label="Search"
          >
            <Search className="w-4 h-4" />
          </button>

          <ThemeToggle />

          {/* Notifications */}
          <Popover.Root open={notifOpen} onOpenChange={setNotifOpen}>
            <Popover.Trigger asChild>
              <button
                className={cn(
                  'relative w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                  notifOpen
                    ? 'bg-cosmic-aurora text-star-white'
                    : 'text-star-muted hover:bg-cosmic-dusk hover:text-star-white',
                )}
                aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-gold"
                    aria-hidden="true"
                  />
                )}
              </button>
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                align="end"
                sideOffset={8}
                className={cn(
                  'z-50 w-80 rounded-2xl overflow-hidden',
                  'bg-cosmic-nebula/95 backdrop-blur-xl',
                  'ring-1 ring-white/10',
                  'shadow-[0_16px_48px_rgba(0,0,0,0.6)]',
                  'animate-scale-in',
                  'focus:outline-none',
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                  <span className="text-sm font-semibold text-star-bright">Notifications</span>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-2xs text-gold hover:text-gold-bright transition-colors"
                      >
                        Mark all read
                      </button>
                    )}
                    <Popover.Close asChild>
                      <button className="w-6 h-6 rounded-md flex items-center justify-center text-star-dim hover:text-star hover:bg-cosmic-dusk transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </Popover.Close>
                  </div>
                </div>

                {/* Notification list */}
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <Bell className="w-8 h-8 mx-auto text-star-dim/30 mb-3" />
                      <p className="text-sm text-star-dim">No notifications</p>
                    </div>
                  ) : (
                    notifications.map((n, i) => (
                      <button
                        key={n.id}
                        onClick={() => setNotifications(ns => ns.map(x => x.id === n.id ? { ...x, read: true } : x))}
                        className={cn(
                          'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-cosmic-dusk/50',
                          i < notifications.length - 1 && 'border-b border-white/[0.04]',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors',
                            n.read ? 'bg-transparent' : 'bg-gold',
                          )}
                          aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1">
                          <p className={cn('text-xs font-medium truncate', n.read ? 'text-star-muted' : 'text-star-bright')}>
                            {n.title}
                          </p>
                          <p className="text-2xs text-star-dim mt-0.5 line-clamp-2 leading-relaxed">{n.body}</p>
                          <p className="text-2xs text-star-dim/50 mt-1 font-mono">{n.time}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>

          {/* Avatar + user dropdown */}
          <DropdownMenu>
            <DropdownTrigger asChild>
              <button
                className="flex items-center gap-2 pl-1.5 pr-1 py-1 rounded-lg
                           hover:bg-cosmic-dusk transition-colors"
                aria-label="User menu"
              >
                <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-violet/40">
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.displayName}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-violet-dark to-violet-mid flex items-center justify-center">
                      <span className="text-star-white text-2xs font-semibold">
                        {user ? getInitials(user.displayName) : '?'}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            </DropdownTrigger>

            <DropdownContent align="end" sideOffset={8}>
              {/* User info */}
              <div className="px-2.5 py-2 mb-1">
                <p className="text-sm font-medium text-star-bright truncate max-w-[180px]">
                  {user?.displayName ?? 'Account'}
                </p>
                <p className="text-2xs text-star-dim truncate max-w-[180px]">{user?.email ?? ''}</p>
              </div>
              <DropdownSeparator />

              <DropdownItem onSelect={() => navigate('/settings')}>
                <User className="w-3.5 h-3.5" />
                Profile &amp; Settings
              </DropdownItem>
              <DropdownItem onSelect={() => navigate('/billing')}>
                <CreditCard className="w-3.5 h-3.5" />
                Billing
              </DropdownItem>

              <DropdownSeparator />

              <DropdownItem
                onSelect={handleSignOut}
                className="text-inauspicious focus:bg-inauspicious/10 focus:text-inauspicious"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </DropdownItem>
            </DropdownContent>
          </DropdownMenu>
        </div>
      </header>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </>
  )
}
